import numeral from 'numeral';

import type { ICampaignUsageTotal, IDailyUsageTotal, IEmailUsageRecord } from '@feathr/blackbox';
import { formatShortDate, humanMonth, shortHumanDate } from '@feathr/hooks';

import type {
  ITransformedAreaChartData,
  ITransformedCampaignData,
  ITransformedMonthlyData,
} from './EmailLimits.types';

/**
 * Transforms campaign data with human-readable values
 */
export function transformCampaignData(
  campaignUsage: ICampaignUsageTotal,
): ITransformedCampaignData {
  return {
    id: campaignUsage.id.toString(),
    name: campaignUsage.campaign.name,
    project: campaignUsage.campaign.event,
    type: campaignUsage.campaign._cls,
    humanCreatedAt: formatShortDate(campaignUsage.campaign.date_created),
    humanStartDate: formatShortDate(campaignUsage.campaign.date_start),
    humanEndDate: formatShortDate(campaignUsage.campaign.date_end),
    humanEmailsSent: numeral(campaignUsage.total).format('0,0'),
    total: campaignUsage.total,
  };
}

/**
 * Transforms monthly data with human-readable values
 */
export function transformMonthlyData(usageRecord: IEmailUsageRecord): ITransformedMonthlyData {
  const { record_date: recordDate, billing, total } = usageRecord;

  const emailSendLimit = billing?.quota ?? 0;
  const percentage = billing.quota > 0 ? Math.round((total / billing.quota) * 100) : 0;
  const incrementalCosts = billing.unit_price * billing.unit_quantity;

  return {
    id: usageRecord.id,
    record_date: recordDate,
    percentage,
    humanMonth: humanMonth(recordDate),
    humanSends: total.toString(),
    humanLimit: emailSendLimit.toString(),
    humanSummary: `${numeral(total).format('0,0')} / ${numeral(emailSendLimit).format('0,0')}`,
    humanPercentage: `${percentage}%`,
    humanIncrementalCosts: incrementalCosts > 0 ? `$${incrementalCosts.toFixed(2)}` : '-',
    incrementalCosts,
  };
}

/**
 * Groups data by frequency and aggregates values
 * @param data - Array of data points with date and value
 * @param frequency - The frequency to group by ('week', 'month', 'year' in future)
 * @returns Grouped and aggregated data
 */
export function transformToFrequency<T extends { date: string; value: number }>(
  data: T[],
  frequency: 'week',
): T[] {
  if (frequency !== 'week') {
    // For now, only week is supported
    return data;
  }

  // Group by week
  const weeklyData = new Map<string, { sum: number; count: number; firstDate: string }>();

  data.forEach((item) => {
    // Parse date as UTC to avoid timezone issues
    const [year, month, day] = item.date.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));

    // Get the Monday of the week
    const dayOfWeek = date.getUTCDay();
    const monday = new Date(date);
    monday.setUTCDate(date.getUTCDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));

    // Format as YYYY-MM-DD
    const weekKey = monday.toISOString().split('T')[0];

    if (!weeklyData.has(weekKey)) {
      weeklyData.set(weekKey, { sum: 0, count: 0, firstDate: item.date });
    }

    const week = weeklyData.get(weekKey)!;
    week.sum += item.value;
    week.count += 1;
  });

  // Convert back to array with averaged values
  return Array.from(weeklyData.entries()).map(([weekKey, weekData]) => ({
    // Keep the structure of the original data
    ...data[0],
    date: weekKey,
    value: Math.round(weekData.sum / weekData.count),
  })) as T[];
}

/**
 * Determines if a date should show a label (weekly intervals from the 1st)
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns true if the date is 1st, 8th, 15th, 22nd, or 29th of the month
 */
export function shouldShowWeeklyLabel(dateString: string): boolean {
  // Handle both YYYY-MM-DD and ISO date string formats
  const day = dateString.includes('T')
    ? Number(dateString.split('T')[0].split('-')[2])
    : Number(dateString.split('-')[2]);
  // Show labels on 1st, 8th, 15th, 22nd, 29th
  return day === 1 || day === 8 || day === 15 || day === 22 || day === 29;
}

/**
 * Transforms area chart data with human-readable values
 */
export function transformAreaChartData(chartData: IDailyUsageTotal): ITransformedAreaChartData {
  const shouldShowLabel = shouldShowWeeklyLabel(chartData.usage_date);
  return {
    date: shouldShowLabel ? shortHumanDate(chartData.usage_date) : '',
    value: chartData.total,
    humanValue: chartData.total.toString(),
    metadata: {
      date: chartData.usage_date,
    },
  };
}
